"use client";

import {
  DEFAULT_FOLDER_ID,
  FOLDER_TRASH_ID,
  isVirtualFolderNotDefault,
  parseFolderId,
} from "@zapier/utils";
import { useFolderTreeFolder } from "../../folderTree/useFolderTreeFolder";
import { Zap } from "../../zap/useSearchZaps";
import { Response, defaultResponse } from "./useZapActionPermissions";
import { useCallback, useEffect, useMemo } from "react";
import { useCheckPermissions } from "../useCheckPermissions";
import {
  HydratedZapAsset,
  useFolderActionPermissions,
  useSupportedAssets,
} from "../../..";

interface Input {
  zap: Zap | HydratedZapAsset | null;
  customuserId: number | undefined;
  isAccountOwner: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isImportExportEnabled: boolean;
  enabled?: boolean;
}

export function useFolderZapActionPermissions({
  zap,
  customuserId,
  isAccountOwner,
  isSuperAdmin,
  isAdmin,
  isImportExportEnabled,
  enabled = true,
}: Input): Response | undefined {
  // ------------------------------------------------------------
  // Folder
  // ------------------------------------------------------------
  const folderId = parseFolderId(zap?.folderId);
  const { folder } = useFolderTreeFolder({ folderId, enabled });
  const folderIsShared = !!folder?.isShared;
  const {
    canCreateZap: canCreateZapInFolder,
    enablePermissionsCheck: enableAdvancedFolderPermissions,
  } = useFolderActionPermissions({
    folderId,
  });

  // ------------------------------------------------------------
  // MPF
  // ------------------------------------------------------------
  const { isMPFEnabled } = useSupportedAssets();

  // ------------------------------------------------------------
  // Permissions Checking
  // ------------------------------------------------------------
  const enableAdvancedPermissions = useCallback(() => {
    enableAdvancedFolderPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we do want zapId and folderId to be dependencies
  }, [zap?.id, folderId, enableAdvancedFolderPermissions]);

  const {
    checkPermissions,
    permissions: _permissions,
    isLoading: arePermissionsLoading,
  } = useCheckPermissions({
    assetId: folderId,
    assetType: "folder",
    permissionLevels: ["can_edit", "can_delete"],
  });

  // Make sure the home folder gets permissions info
  const permissions = useMemo(() => {
    return folderId === DEFAULT_FOLDER_ID
      ? { canEdit: true, canDelete: true }
      : _permissions;
  }, [_permissions, folderId]);

  const isZapOwner =
    customuserId !== undefined && customuserId === zap?.customuserId;
  // Zap owner, account owner, or super admin — these roles have all permissions
  // granted via the fast path below, so no Permissions API call is needed.
  const isAssetOwnerPlus = isZapOwner || isAccountOwner || isSuperAdmin;

  // Only fetch from the Permissions API for users who need it (admins, members).
  // isAssetOwnerPlus users already have all permissions via the fast path.
  // Skip for project Zaps — they use a different permission model (useProjectZapActionPermissions).
  useEffect(() => {
    const shouldCheckPermissions =
      folderId &&
      isMPFEnabled &&
      !zap?.projectId &&
      !isAssetOwnerPlus &&
      !permissions &&
      !arePermissionsLoading;
    if (shouldCheckPermissions) {
      checkPermissions();
    }
  }, [
    arePermissionsLoading,
    checkPermissions,
    zap,
    folderId,
    isMPFEnabled,
    isAssetOwnerPlus,
    permissions,
  ]);

  // ------------------------------------------------------------
  // Permissions
  // ------------------------------------------------------------

  /** Don't parse folder permissions if this Zap is in a project */
  if (zap?.projectId) {
    return;
  }
  if (!enabled) {
    return defaultResponse;
  }

  const zapIsTrashed = folderId === FOLDER_TRASH_ID || !!zap?.deletedAt;
  const zapIsInProject = !!zap?.projectId;
  const zapIsInVirtualFolder =
    !zapIsInProject && (isVirtualFolderNotDefault(folderId) || zapIsTrashed);

  // Is this a personal Zap that is unshared?
  const isPrivateZap = () => {
    // only zap owner, account owner, and super admin can interact with personal Zaps
    if (!folderIsShared && !isAssetOwnerPlus) return false;
    return true;
  };

  const canChangeOwner = () => {
    if (isMPFEnabled) {
      return !zapIsTrashed && isAssetOwnerPlus;
    }
    if (!isPrivateZap()) return false;
    // Zaps need to be in a shared folder to have ownership changed
    if (
      folderIsShared &&
      (isAccountOwner || isSuperAdmin || isAdmin || isZapOwner)
    )
      return true;
    return false;
  };

  const canDelete = () => {
    if (isMPFEnabled) {
      // if the user has permissions to delete or is the asset owner, they can delete
      return (
        (!!permissions?.canDelete || isAssetOwnerPlus) && !zapIsInVirtualFolder
      );
    }
    if (!isPrivateZap()) return false;
    return !zapIsInVirtualFolder;
  };

  const canDuplicate = () => {
    if (!canCreateZapInFolder) return false;
    if (isMPFEnabled) {
      return (!!permissions?.canEdit || isAssetOwnerPlus) && !zapIsTrashed;
    }
    if (!isPrivateZap()) return false;
    return !zapIsInVirtualFolder;
  };

  const canCreateTemplate = () => {
    if (!canCreateZapInFolder) return false;
    if (isMPFEnabled) {
      return (!!permissions?.canEdit || isAssetOwnerPlus) && !zapIsTrashed;
    }
    if (!isPrivateZap()) return false;
    return !zapIsInVirtualFolder;
  };

  const canEdit = () => {
    if (isMPFEnabled) {
      return (!!permissions?.canEdit || isAssetOwnerPlus) && !zapIsTrashed;
    }
    if (!isPrivateZap()) return false;
    return !zapIsTrashed;
  };

  const canEnableDisable = () => {
    if (isMPFEnabled) {
      return (
        (!!permissions?.canEdit || isAssetOwnerPlus) && !zapIsInVirtualFolder
      );
    }
    return !zapIsInVirtualFolder;
  };

  const canExport = () => {
    if (isMPFEnabled) {
      return (
        (!!permissions?.canEdit || isAssetOwnerPlus) &&
        isImportExportEnabled &&
        !zapIsTrashed
      );
    }
    if (!isPrivateZap()) {
      return false;
    }
    return (
      isImportExportEnabled && !zapIsTrashed && (isZapOwner || folderIsShared)
    );
  };

  const canMoveZapToFolderOrProject = () => {
    if (isMPFEnabled) {
      return (
        (!!permissions?.canEdit || isAssetOwnerPlus) &&
        (!zapIsInVirtualFolder || zapIsTrashed)
      );
    }
    if (!isPrivateZap()) return false;
    return (
      (!zapIsInVirtualFolder || zapIsTrashed) &&
      (isZapOwner || folderIsShared || isAccountOwner || isSuperAdmin)
    );
  };

  const canRename = () => {
    if (isMPFEnabled) {
      return (!!permissions?.canEdit || isAssetOwnerPlus) && !zapIsTrashed;
    }
    if (!isPrivateZap()) return false;
    return !zapIsTrashed;
  };

  const canShare = () => {
    if (isMPFEnabled) {
      return (
        (!!permissions?.canEdit || isAssetOwnerPlus) && !zapIsInVirtualFolder
      );
    }
    if (!isPrivateZap()) return false;
    if (!zapIsInVirtualFolder && (isZapOwner || isSuperAdmin || isAccountOwner))
      return true;
    return !zapIsInVirtualFolder && folderIsShared;
  };

  const canShowDrafts = () => {
    return !zapIsInVirtualFolder;
  };

  const slackWorkflowBuilderUrl =
    (zap?.sourceUrl?.includes("app.slack.com") && zap?.sourceUrl) || undefined;

  return {
    ...defaultResponse,
    canChangeOwner: canChangeOwner(),
    canCreateTemplate: canCreateTemplate(),
    canDelete: canDelete(),
    canDuplicate: canDuplicate(),
    canViewZapHistory: true, // anyone with view access
    canEdit: canEdit(),
    canEnableDisable: canEnableDisable(),
    canExport: canExport(),
    canMoveZapToFolder: canMoveZapToFolderOrProject(),
    canMoveZapToProject: canMoveZapToFolderOrProject(),
    canRename: canRename(),
    canShare: canShare(),
    canShowDrafts: canShowDrafts(),
    enableAdvancedPermissions,
    slackWorkflowBuilderUrl,
  };
}
